import React, { useEffect, useRef, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import useGooglePlacesAutocomplete from '../../../hooks/useGooglePlacesAutocomplete';
import { ClientResponse } from '../../../redux/api/customer/customer.model';
import StringUtils from '../../../utils/stringUtils';
import { useGetCustomerByIdQuery } from '../../../redux/api/customer/customerApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import { LoadLocation } from '../../../redux/api/load/load.model';
import '../../CreateInvoice/CreateInvoice.scss';
import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';
import { AmplitudeEvent, logAmplitudeEvent } from '../../../utils/amplitudeUtils';

export interface BillToData {
  placeId?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  zip?: string;
  placeName?: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  clientId: string;
  withFactoringAssignment?: boolean;
}
export type TooltipType = 'info' | 'warning' | 'error' | 'success';
export interface BillToProps {
  clientId: string;
  onBillToEdited?: (billData: BillToData) => void;
  onNewLocation?: (location: LoadLocation) => void;
  onBillingEmailChange?: (id: string) => void;
  tooltipString?: string;
  tooltipType?: TooltipType;
}

// TODO: (fix) onBillToEdited is being fired a lot of unnecessary times (14 times in dev env probably 7 without strict mode)
const BillTo: React.FC<BillToProps> = ({
  clientId,
  onBillToEdited,
  onNewLocation,
  onBillingEmailChange,
  tooltipString,
  tooltipType = 'info'
}): JSX.Element => {
  const { init: initAutocomplete } = useGooglePlacesAutocomplete();

  const [client, setClient] = useState<ClientResponse | undefined>(undefined);
  const autocompleteInputRef = useRef<HTMLInputElement | undefined>();
  const isAutocompleteInUseRef = useRef<boolean>(false);
  const [googleAutocompleteText, setGoogleAutocompleteText] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');
  const [clientPhone, setClientPhone] = useState<string>('');

  // Edit client dialog
  const [editClientOpen, setEditClientOpen] = useState(false);

  // Manually added location
  const [clientCreatedLocation, setClientCreatedLocation] = useState<LoadLocation | undefined>(
    undefined
  );

  // Google place data

  // RTK Clients
  const { data: clientData } = useGetCustomerByIdQuery(clientId);

  const { addNotification } = useNotifications();

  const onGooglePlaceChanged = (place?: google.maps.places.PlaceResult): void => {
    if (!place || !place.address_components) return;
    isAutocompleteInUseRef.current = true;

    logAmplitudeEvent(AmplitudeEvent.GOOGLE_PLACES_REQUEST);
    let address = '';
    let city = '';
    let state = '';
    let zip = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          address = `${component.long_name} ${address}`;
          break;
        case 'route':
          address += component.short_name;
          break;
        case 'postal_code':
          zip = `${component.long_name}${zip}`;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name;
          break;
      }
    }

    setGoogleAutocompleteText(place.formatted_address || '');

    const billToData: BillToData = {
      placeId: place.place_id || '',
      addressLine: place.formatted_address || '',
      city: city,
      state: state,
      zip: zip,
      placeName: place.name || `${place.formatted_address || address}, ${city}, ${state}`,
      clientName: clientName,
      clientEmail: clientEmail,
      clientPhone: clientPhone,
      clientId: clientId
    };

    // If address is selected from autocomplete, this returns client data to be saved
    // when Invoice is submitted
    onBillToEdited && onBillToEdited(billToData);
    isAutocompleteInUseRef.current = false;
  };

  const handleClientEditOpen = (): void => {
    setEditClientOpen(true);
  };

  const handleClientEditClose = (): void => {
    setEditClientOpen(false);
  };

  /** * Sets client data */
  useEffect(() => {
    if (clientData) {
      try {
        setClient(clientData);
      } catch (error) {
        addNotification({ message: "Couldn't get customer info", variant: 'error' });
      }
    }
  }, [clientData]);

  useEffect(() => {
    const autocompleteTimeout = setTimeout(() => {
      // Init Google autocomplete
      initAutocomplete({
        inputRef: autocompleteInputRef,
        onPlaceChanged: onGooglePlaceChanged
      });

      autocompleteInputRef.current?.focus();
    }, 100);

    return (): void => {
      clearTimeout(autocompleteTimeout);
    };
  }, []);

  useEffect(() => {
    // If all data is present for the given client, we return the mandatory fields
    // to prevent errors in validations when invoice submits

    const billToData: BillToData = {
      clientName: client?.name || '',
      clientEmail: client?.billing_email
        ? StringUtils.removeBlankSpacesFrom(client?.billing_email)
        : '',
      clientId: clientId,
      clientPhone: client?.primary_phone
    };

    client?.location && setGoogleAutocompleteText(client?.location.address_1);
    client?.name && setClientName(client?.name);
    client?.primary_phone && setClientPhone(client?.primary_phone);
    client?.billing_email &&
      setClientEmail(StringUtils.removeBlankSpacesFrom(client.billing_email));

    if (client?.id && (client?.billing_email !== clientEmail || client?.name !== clientName)) {
      onBillingEmailChange && onBillingEmailChange(client?.id);
    }

    onBillToEdited && onBillToEdited(billToData);
  }, [client]);

  useEffect(() => {
    clientCreatedLocation && onNewLocation && onNewLocation(clientCreatedLocation);
  }, [clientCreatedLocation]);

  const getTooltipIcon = (type?: TooltipType): JSX.Element => {
    switch (type) {
      case 'info':
        return (
          <InfoIcon
            sx={{
              fill: '#D1D5DB',
              height: '18px',
              width: '18px'
            }}
          />
        );
      case 'warning':
        return (
          <WarningIcon
            sx={{
              fill: '#FFB342',
              height: '18px',
              width: '18px'
            }}
          />
        );
      case 'error':
        return (
          <ErrorIcon
            sx={{
              fill: '#EF4444',
              height: '18px',
              width: '18px'
            }}
          />
        );
      case 'success':
        return (
          <CheckCircleIcon
            sx={{
              fill: '#10B981',
              height: '18px',
              width: '18px'
            }}
          />
        );
      default:
        return (
          <InfoIcon
            sx={{
              fill: '#D1D5DB',
              height: '18px',
              width: '18px'
            }}
          />
        );
    }
  };

  return (
    <div className="create-invoice__bill-to">
      <div className="create-invoice__bill-header">
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-start"
          alignItems="center"
          marginBottom="14px"
        >
          <Typography variant="body1" fontWeight="600" color="#1F2937" fontSize="18px">
            Bill to
          </Typography>

          {tooltipString && (
            <TwTooltip title={tooltipString} placement="top">
              {getTooltipIcon(tooltipType)}
            </TwTooltip>
          )}
        </Stack>

        <span className="create-invoice__bill-edit-link" onClick={handleClientEditOpen}>
          Edit customer info
        </span>
      </div>
      <span className="create-invoice__bill-name">{client?.name}</span>
      {client?.location?.full_address && (
        <span className="create-invoice__bill-description">{client?.location?.full_address}</span>
      )}
      <span className="create-invoice__bill-description">{client?.billing_email}</span>
      <span className="create-invoice__bill-description">
        {client?.primary_phone && StringUtils.formatPhoneNumber(client?.primary_phone)}
      </span>

      <TwEditCompaniesModal
        entityId={clientId}
        open={editClientOpen}
        onClose={handleClientEditClose}
        entity={CompaniesEntities.customer}
      />
    </div>
  );
};

export default BillTo;
